import React, { useState, useEffect } from 'react';
import { exerciseAPI } from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, RotateCw } from 'lucide-react';
import { FlashcardExercise } from './FlashcardExercise';
import { ImageChoiceExercise } from './ImageChoiceExercise';
import { MultipleChoiceExercise } from './MultipleChoiceExercise';

// Функция перемешивания массива
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Экран финальных результатов
const LessonResultsScreen = ({ 
  correct = 0,
  incorrect = 0,
  onRestart,
  onRestartCorrect,
  onRestartIncorrect,
  onBack,
  topic
}) => {
  const total = correct + incorrect;
  const percentage = total > 0 ? (correct / total) * 100 : 0;
  const { t } = useLanguage();

  return (
    <div className="results-page">
      <div className="results-container">
        
        {/* Заголовок */}
        <div className="results-header">
          <h1 className="results-title">🎉 {t('lessonComplete')}!</h1>
          <p className="results-subtitle">{topic?.germanTitle}</p>
        </div>

        {/* Два столбика со статистикой */}
        <div className="results-stats-grid">
          
          {/* Правильно */}
          <div className="results-stat-card correct">
            <div className="results-stat-number">{correct}</div>
            <div className="results-stat-label">{t('answersCorrect')}</div>
            <div className="results-stat-pct">
              {total > 0 ? (correct / total * 100).toFixed(0) : 0}% {t('ofAll')}
            </div>
            {correct > 0 && (
              <button onClick={onRestartCorrect} className="results-stat-btn">
                <RotateCw size={16} />
                {t('repeatCorrect')}
              </button>
            )}
          </div>

          {/* Неправильно */}
          <div className="results-stat-card incorrect">
            <div className="results-stat-number">{incorrect}</div>
            <div className="results-stat-label">{t('errors')}</div>
            <div className="results-stat-pct">
              {total > 0 ? (incorrect / total * 100).toFixed(0) : 0}% {t('ofAll')}
            </div>
            {incorrect > 0 && (
              <button onClick={onRestartIncorrect} className="results-stat-btn">
                <RotateCw size={16} />
                {t('repeatIncorrect')}
              </button>
            )}
          </div>
        </div>

        {/* Прогресс-бар */}
        <div className="results-progress-section">
          <div className="results-progress-header">
            <span className="results-progress-label">{t('total')}:</span>
            <span className="results-progress-value">{Math.round(percentage)}%</span>
          </div>
          <div className="results-progress-track">
            <div className="results-progress-fill" style={{ width: `${percentage}%` }} />
          </div>
        </div>

        {/* Общая статистика */}
        <div className="results-detail-card">
          <h3 className="results-detail-title">📊 {t('total')}:</h3>
          <div className="results-detail-row">
            <span className="results-detail-label">{t('totalQuestions')}:</span>
            <span className="results-detail-value">{total}</span>
          </div>
          <div className="results-detail-row" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 12 }}>
            <span className="results-detail-label">{t('answersCorrect')}:</span>
            <span className="results-detail-value" style={{ color: 'var(--mint)' }}>{correct}</span>
          </div>
          <div className="results-detail-row">
            <span className="results-detail-label">{t('errors')}:</span>
            <span className="results-detail-value" style={{ color: 'var(--coral)' }}>{incorrect}</span>
          </div>
        </div>

        {/* Кнопки */}
        <div className="results-actions">
          <button onClick={onRestart} className="results-btn-primary">
            <RotateCw size={20} />
            {t('repeatAll')}
          </button>
          <button onClick={onBack} className="results-btn-secondary">
            🚪 {t('exit')}
          </button>
        </div>

        {/* Совет */}
        <div className="results-tip">
          💡 <strong>{t('greatWork')}!</strong> {t('nextLesson')}!
        </div>
      </div>
    </div>
  );
};

export const ExerciseComponent = ({ topic, onBack, onProgressUpdate }) => {
  const { t } = useLanguage();
  const [exercises, setExercises] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalIncorrect, setTotalIncorrect] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [answerMap, setAnswerMap] = useState({});

  useEffect(() => {
    // Сбрасываем всё при смене топика
    setTotalCorrect(0);
    setTotalIncorrect(0);
    setCurrentExerciseIndex(0);
    setIsFinished(false);
    setAnswerMap({});
    setExercises([]);
    setAllExercises([]);
    setLoading(true);
    fetchExercises();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic.id]);

  const fetchExercises = async () => {
    try {
      const response = await exerciseAPI.getExercises(topic.id);
      // Загружаем все упражнения (flashcard, imageChoice и multipleChoice) и перемешиваем
      const filteredExercises = response.data.filter(ex => 
        ex.type === 'flashcard' || ex.type === 'imageChoice' || ex.type === 'multipleChoice'
      );
      const shuffledExercises = shuffleArray(filteredExercises);
      setAllExercises(filteredExercises);
      setExercises(shuffledExercises);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="exercise-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p style={{ color: 'rgba(245,244,240,0.5)', fontSize: 18 }}>{t('loading')}</p></div>;
  }

  if (exercises.length === 0) {
    return (
      <div className="exercise-page" style={{ padding: 32 }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <button onClick={onBack} className="ticket-back-btn" style={{ marginBottom: 16 }}>
            <ArrowLeft size={16} />
            <span>{t('back')}</span>
          </button>
          <div className="results-tip">
            <p>{t('noExercises')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Экран финальных результатов
  if (isFinished) {
    return (
      <LessonResultsScreen
        correct={totalCorrect}
        incorrect={totalIncorrect}
        onRestart={() => {
          setCurrentExerciseIndex(0);
          setTotalCorrect(0);
          setTotalIncorrect(0);
          setIsFinished(false);
          setAnswerMap({});
          // Перемешиваем упражнения заново
          const shuffledExercises = shuffleArray(allExercises);
          setExercises(shuffledExercises);
        }}
        onRestartCorrect={() => {
          const filteredExercises = buildFilteredExercises('correct');
          if (filteredExercises.length === 0) {
            return;
          }
          setCurrentExerciseIndex(0);
          setTotalCorrect(0);
          setTotalIncorrect(0);
          setIsFinished(false);
          setAnswerMap({});
          setExercises(filteredExercises);
        }}
        onRestartIncorrect={() => {
          const filteredExercises = buildFilteredExercises('incorrect');
          if (filteredExercises.length === 0) {
            return;
          }
          setCurrentExerciseIndex(0);
          setTotalCorrect(0);
          setTotalIncorrect(0);
          setIsFinished(false);
          setAnswerMap({});
          setExercises(filteredExercises);
        }}
        onBack={() => {
          onProgressUpdate();
          onBack();
        }}
        topic={topic}
      />
    );
  }

  const exercise = exercises[currentExerciseIndex];
  
  const handleAnswerSubmit = (result) => {
    if (result?.flashcardSummary && result?.exerciseId) {
      const { correct = 0, incorrect = 0, correctIndices = [], incorrectIndices = [] } = result.flashcardSummary;
      setTotalCorrect((prev) => prev + correct);
      setTotalIncorrect((prev) => prev + incorrect);
      setAnswerMap((prev) => ({
        ...prev,
        [result.exerciseId]: {
          type: 'flashcard',
          correct: correctIndices,
          incorrect: incorrectIndices
        }
      }));
      return;
    }

    // Оба типа упражнений теперь отправляют { isCorrect: true/false }
    if (result.isCorrect === true) {
      setTotalCorrect((prev) => prev + 1);
    } else if (result.isCorrect === false) {
      setTotalIncorrect((prev) => prev + 1);
    }

    if (!result.exerciseId) {
      return;
    }

    setAnswerMap((prev) => {
      const next = { ...prev };
      const exerciseId = result.exerciseId;

      if (typeof result.cardIndex === 'number') {
        const entry = next[exerciseId] || { type: 'flashcard', correct: [], incorrect: [] };
        const correct = entry.correct || [];
        const incorrect = entry.incorrect || [];
        const updatedEntry = {
          type: 'flashcard',
          correct: result.isCorrect
            ? (correct.includes(result.cardIndex) ? correct : [...correct, result.cardIndex])
            : correct,
          incorrect: !result.isCorrect
            ? (incorrect.includes(result.cardIndex) ? incorrect : [...incorrect, result.cardIndex])
            : incorrect
        };

        next[exerciseId] = updatedEntry;
        return next;
      }

      next[exerciseId] = { type: 'imageChoice', isCorrect: result.isCorrect === true };
      return next;
    });
  };

  function buildFilteredExercises(mode) {
    return exercises.reduce((acc, exerciseItem) => {
      const entry = answerMap[exerciseItem.id];
      if (!entry) {
        return acc;
      }

      if (exerciseItem.type === 'flashcard') {
        const indices = mode === 'correct' ? entry.correct : entry.incorrect;
        if (!indices || indices.length === 0) {
          return acc;
        }
        const pairs = exerciseItem.pairs || exerciseItem.content?.pairs || [];
        const filteredPairs = pairs.filter((_, idx) => indices.includes(idx));
        if (filteredPairs.length === 0) {
          return acc;
        }
        acc.push({ ...exerciseItem, pairs: filteredPairs });
        return acc;
      }

      if (exerciseItem.type === 'imageChoice') {
        const shouldInclude = mode === 'correct' ? entry.isCorrect : !entry.isCorrect;
        if (shouldInclude) {
          acc.push(exerciseItem);
        }
      }

      if (exerciseItem.type === 'multipleChoice') {
        const shouldInclude = mode === 'correct' ? entry.isCorrect : !entry.isCorrect;
        if (shouldInclude) {
          acc.push(exerciseItem);
        }
      }

      return acc;
    }, []);
  }

  const handleExerciseBack = () => {
    if (currentExerciseIndex < exercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
    } else {
      // Все упражнения завершены
      setIsFinished(true);
    }
  };

  // Рендеруем упражнение в зависимости от типа
  if (exercise.type === 'flashcard') {
    return (
      <FlashcardExercise
        key={`ex-${exercise.id}-${currentExerciseIndex}`}
        topic={topic}
        exercise={exercise}
        onAnswer={handleAnswerSubmit}
        onBack={handleExerciseBack}
        onExit={onBack}
        showResultsScreen={false}
        currentExerciseIndex={currentExerciseIndex}
        totalExercises={exercises.length}
      />
    );
  }

  if (exercise.type === 'imageChoice') {
    return (
      <ImageChoiceExercise
        key={`ex-${exercise.id}-${currentExerciseIndex}`}
        topic={topic}
        exercise={exercise}
        onAnswerSubmit={handleAnswerSubmit}
        onBack={handleExerciseBack}
        onExit={onBack}
        showResultsScreen={false}
        totalCorrect={totalCorrect}
        totalIncorrect={totalIncorrect}
        currentExerciseIndex={currentExerciseIndex}
        totalExercises={exercises.length}
      />
    );
  }

  if (exercise.type === 'multipleChoice') {
    return (
      <MultipleChoiceExercise
        key={`ex-${exercise.id}-${currentExerciseIndex}`}
        topic={topic}
        exercise={exercise}
        onAnswerSubmit={handleAnswerSubmit}
        onBack={handleExerciseBack}
        onExit={onBack}
        totalCorrect={totalCorrect}
        totalIncorrect={totalIncorrect}
        currentExerciseIndex={currentExerciseIndex}
        totalExercises={exercises.length}
      />
    );
  }

  return (
    <div className="exercise-page" style={{ padding: 32 }}>
      <p style={{ textAlign: 'center', color: 'rgba(245,244,240,0.4)' }}>
        Неизвестный тип упражнения: {exercise.type}
      </p>
    </div>
  );
};

export default ExerciseComponent;
